import { useState, useEffect, useCallback } from "react";
import { Query } from 'appwrite';
import api from "../../api/api";
import {
  FetchState,
  useGetGameId,
  useGetGameCode,
  useGetGameState,
  useGetGameParticipants,
  useGetGameJudges,
  useGetPhotoCards,
  useGetCaptions,
  useGetPlayerCaption,
  useGetScores,
} from '../../hooks';
import { Server } from '../../utils/config';
import Phase1 from '../../components/MemeGame/Phase1';
import Phase2 from '../../components/MemeGame/Phase2';
import Phase3 from '../../components/MemeGame/Phase3';
import Phase4 from '../../components/MemeGame/Phase4';
import Phase5 from '../../components/MemeGame/Phase5';
import Phase6 from '../../components/MemeGame/Phase6';
import Leaderboard from '../../components/Leaderboard';
import {getJudgeName} from "../../utils";


const MemeGame = () => {
  const [stale, setStale] = useState({ stale: false });
  const [{ isLoadingGames, isErrorGames, gameId }] = useGetGameId(stale);
  const [{ isLoadingGameCodes, isErrorGameCodes, gameCode }] = useGetGameCode(stale, gameId);
  const [{ isLoadingGameState, isErrorGameState, gameState }] = useGetGameState(stale, gameCode);

  const [memeGameStateDocumentId, setMemeGameStateDocumentId] = useState('');
  const [participantGameCode, setParticipantGameCode] = useState('');
  const [phase, setPhase] = useState(gameState?.Phase || 1);
  const [judge, setJudge] = useState({});
  const [judgeId, setJudgeId] = useState('');
  const [recurrence, setRecurrence] = useState(1);
  const [participantName, setParticipantName] = useState('');
  const [photoCardId, setPhotoCardId] = useState('');
  const [captionInput, setCaptionInput] = useState('');

  const [{ isLoadingGameParticipants, isErrorGameParticipants, gameParticipants }] = useGetGameParticipants(stale, gameCode);
  const [{ isLoadingGameJudges, isErrorGameJudges, gameJudges }] = useGetGameJudges(stale, gameCode, recurrence);
  const [{ isLoadingPhotoCards, isErrorPhotoCards, photoCards }] = useGetPhotoCards(stale);
  const [{ isLoadingCaptions, isErrorCaptions, captions }] = useGetCaptions(
    stale,
    gameCode,
    (photoCardId || gameState?.PhotoCardID),
  );
  const [{ isLoadingPlayerCaption, isErrorPlayerCaption, playerCaption }] = useGetPlayerCaption(
    stale,
    gameCode,
    participantName,
    (photoCardId || gameState?.PhotoCardID)
  );
  const [{ isLoadingScores, isErrorScores, scores }] = useGetScores(stale, gameCode);


  const renderPhase = useCallback((
    handleGameStateChanged,
    handleSetStale,
    handleSetRecurrence,
    handleSetParticipantGameCode,
    handleSetParticipantName,
    handleSetPhase,
    handleSetJudge,
    handleSetJudgeId,
    handleUpdatePhotoCard,
    handleCaptionCreate,
    handleCaptionSelected,
    handleJudgeCreated,
    handleSetCaptionInput,
    handleGameCodeChanged,
    handleStartNewGame,
  ) => {
    switch (phase) {
      case 1:
        return (
          <Phase1
            handleGameStateChanged={handleGameStateChanged}
            handleSetStale={handleSetStale}
            handleSetParticipantGameCode={handleSetParticipantGameCode}
            handleSetParticipantName={handleSetParticipantName}
            handleSetPhase={handleSetPhase}
            participantGameCode={participantGameCode}
            participantName={participantName}
            phase={phase}
            gameCode={gameCode}
            gameParticipants={gameParticipants}
          />
        )
      case 2:
        return (
          <Phase2
            handleGameStateChanged={handleGameStateChanged}
            handleSetStale={handleSetStale}
            handleSetRecurrence={handleSetRecurrence}
            handleSetPhase={handleSetPhase}
            handleSetJudge={handleSetJudge}
            handleSetJudgeId={handleSetJudgeId}
            handleJudgeCreated={handleJudgeCreated}
            gameCode={gameCode}
            gameParticipants={gameParticipants}
            gameJudges={gameJudges}
            gameState={gameState}
            recurrence={recurrence}
            judge={judge}
            judgeId={judgeId}
          />
        )
      case 3:
        return (
          <Phase3
            handleGameStateChanged={handleGameStateChanged}
            handleSetStale={handleSetStale}
            handleSetPhase={handleSetPhase}
            handleUpdatePhotoCard={handleUpdatePhotoCard}
            gameCode={gameCode}
            gameParticipants={gameParticipants}
            photoCards={photoCards}
            gameState={gameState}
            recurrence={recurrence}
            judge={judge}
          />
        )
      case 4:
        return (
          <Phase4
            handleGameStateChanged={handleGameStateChanged}
            handleSetStale={handleSetStale}
            handleSetPhase={handleSetPhase}
            handleUpdatePhotoCard={handleUpdatePhotoCard}
            handleCaptionCreate={handleCaptionCreate}
            handleSetCaptionInput={handleSetCaptionInput}
            handleSetParticipantName={handleSetParticipantName}
            gameCode={gameCode}
            gameParticipants={gameParticipants}
            participantName={participantName}
            playerCaption={playerCaption}
            photoCards={photoCards}
            captions={captions}
            captionInput={captionInput}
            gameState={gameState}
            recurrence={recurrence}
            judge={judge}
          />
        )
      case 5:
        return (
          <Phase5
            handleGameStateChanged={handleGameStateChanged}
            handleSetStale={handleSetStale}
            handleSetPhase={handleSetPhase}
            handleUpdatePhotoCard={handleUpdatePhotoCard}
            handleCaptionSelected={handleCaptionSelected}
            gameCode={gameCode}
            gameState={gameState}
            gameParticipants={gameParticipants}
            participantName={participantName}
            photoCards={photoCards}
            captions={captions}
            isLoadingCaptions={isLoadingCaptions}
            judge={judge}
          />
        )
      case 6:
        return (
          <Phase6
            handleGameStateChanged={handleGameStateChanged}
            handleSetStale={handleSetStale}
            handleSetPhase={handleSetPhase}
            handleUpdatePhotoCard={handleUpdatePhotoCard}
            gameCode={gameCode}
            gameState={gameState}
            gameParticipants={gameParticipants}
            participantName={participantName}
            photoCards={photoCards}
            judge={judge}
          />
        )
      default:
        return (
          <Leaderboard
            handleGameStateChanged={handleGameStateChanged}
            handleSetStale={handleSetStale}
            handleSetPhase={handleSetPhase}
            handleGameCodeChanged={handleGameCodeChanged}
            handleStartNewGame={handleStartNewGame}
            gameCode={gameCode}
            gameState={gameState}
            gameParticipants={gameParticipants}
            participantName={participantName}
            scores={scores}
          />
        )
    }
  }, [
    gameState,
    gameParticipants,
    gameJudges,
    phase,
    recurrence,
    participantName,
    participantGameCode,
    photoCardId,
    captionInput,
    judge,
    judgeId,
    scores,
    captions,
    playerCaption,
    photoCards,
    gameCode,
    stale]
  );


  const handleSetStale = useCallback(() => {
    setStale({ stale: true });
  });
  const handleSetPhase = useCallback((newPhase) => {
    setPhase(newPhase);
  });
  const handleSetRecurrence = useCallback(() => {
    setRecurrence(recurrence + 1);
  });
  const handleSetParticipantGameCode = useCallback((gc) => {
    setParticipantGameCode(gc);
  });
  const handleSetParticipantName = useCallback((name) => {
    setParticipantName(name);
  });
  const handleSetCaptionInput = useCallback((caption) => {
    setCaptionInput(caption);
  });
  const handleSetJudge = useCallback((newJudge) => {
    setJudge(newJudge);
  });
  const handleSetJudgeId = useCallback((newJudgeId) => {
    setJudgeId(newJudgeId);
  });
  const handleStartNewGame = useCallback(() => {
    localStorage.clear();
    handleGameCodeChanged(true)
      .then(() => {
        setPhase(1);
        setRecurrence(0);
        setParticipantName('');
        setParticipantGameCode('');
        setJudge('');
        setJudgeId('');
        setPhotoCardId('');
        setCaptionInput('');
        setStale({stale: true});
      }).then(() => {
        setStale({stale: true});
      });
});

  const handleGameStateChanged = useCallback(
    async ({newPhase = 0, newJudge = '', newPhoto = ''}) => {
      if (!gameState?.GameCode && gameCode === '') {
        return;
      }

      let stateDocumentId = ''

      try {
        stateDocumentId = await api.listDocuments(
          Server.memeGameStateCollectionId,
          [
            Query.equal("GameCode", [gameCode]),
            Query.notEqual("Deleted", [true])
          ]
        )
        .then((res) => {
          if (res.documents.length > 0) {
            return res
          }
        }, error => {
          console.log(error)
          return error;
        })
        .then((res) => res.documents[0].$id);
      }
      catch (e) {
        console.log("Error getting game state", e);
      }

      let data = {
        GameCode: gameCode,
        Phase: newPhase || phase,
        JudgeID: newJudge || judgeId,
        PhotoCardID: newPhoto || photoCardId,
      }
      if (newPhase === 2 && newJudge === '') {
        data.JudgeID = newJudge
      }
      if (newPhase === 2 && newPhoto === '') {
        data.PhotoCardID = newPhoto
      }
    try {
      if (stateDocumentId === '' || stateDocumentId === undefined || stateDocumentId === null) {
        return;
      }
      if (data.Phase === 0 || (data.Phase > 2 && data.JudgeID === '') || (data.Phase > 3 && data.PhotoCardID === '')) {
        console.log("Invalid game state", data)
        return
      }
      await api.updateDocument(Server.memeGameStateCollectionId, stateDocumentId, data)
        .then((res) => {
          console.log("SUCCESS UPDATING GAME STATE")
        }, (err) => {
          console.log('error', err)
        })

      if (newPhase !== 0) {
        setPhase(newPhase);
      }
    } catch (e) {
      console.log("Error updating game state", e);
    }
    }, [gameCode, gameState?.GameCode, gameId, phase, judgeId, photoCardId]
  );

  // Update the game code after game has finished
  const handleGameCodeChanged = useCallback(
    async (deleted = true) => {
      if (gameCode === '') {
        return;
      }

      console.log("gameCode", gameCode)

      let documentId = ''
      try {
        documentId = await api.listDocuments(
          Server.gameCodesCollectionId,
          [
            Query.equal("Code", [gameCode]),
            Query.notEqual("Deleted", [true])
          ]
        )
        .then((res) => {
          console.log("res", res)
          return res.documents[0];
        }, error => {
          console.log(error)
          return error;
        })
        .then(res => res.$id);

        console.log("documentId", documentId)
      }
      catch (e) {
        console.log("Error getting game state", e);
        return;
      }

      if (!documentId) {
        // alert("No game code was found. Try again, or check with the admin.")
        return;
      }

      let data = {
        Deleted: deleted,
      }
      try {
        await api.updateDocument(Server.gameCodesCollectionId, documentId, data)
          .then((res) => {
            console.log('response', res)
          }, (err) => {
            console.log('error', err)
          })
      } catch (e) {
        console.log("Error updating game state", e);
      }
    }, [gameCode]
  );

  // Update the photo card with the judge's chosen caption and whether it has been used or not
  const handleUpdatePhotoCard = useCallback(
    async (photoCard, hasBeenUsed = false, chosenCaption = null) => {
      if (photoCard?.$id === '') {
        return;
      }

      let data = {
        HasBeenUsed: hasBeenUsed,
      }
      if (chosenCaption !== null) {
        data.ChosenCaption = chosenCaption.Caption;
        data.ChosenCaptionPlayer = chosenCaption.ParticipantName;
      }
      try {
        if (hasBeenUsed === false && chosenCaption === null) {
          return;
        }
        await api.updateDocument(Server.memePhotoCardsCollectionId, photoCard.$id, data)
          .then((res) => {
            console.log('response', res)
          }, (err) => {
            console.log('error', err)
          })
      } catch (e) {
        console.log("Error updating game state", e);
      }
    }, []
  );

  const handleCaptionSelected = useCallback(
    async (playerName, captionOwner, caption, pointsPerWin) => {
      console.log("Caption selected", playerName, captionOwner, caption, pointsPerWin)
      console.log("Game state", gameState)
      if (gameState?.PhotoCardID === '' ||
        getJudgeName(gameState?.JudgeID, gameState, gameParticipants) !== playerName) {
        return;
      }

      let data = {
        GameCode: gameCode,
        GameParticipantID: captionOwner,
        Points: pointsPerWin > 0 ? pointsPerWin : 100,
      }
      try {
        await api.createDocument(Server.gamePointsCollectionId, data)
          .then((res) => {
            console.log('response', res)
          }, (err) => {
            console.log('error', err)
          })
      } catch (e) {
        console.log("Error awarding points", e);
      }
    }, [gameCode, gameState]
  );

  // Create the player's caption for the photo
  const handleCaptionCreate = useCallback(
    async (participantName, photoCardId, playerPhotoCaption) => {
      if (gameCode === '') {
        return;
      }

      let data = {
        GameCode: gameCode,
        ParticipantName: participantName,
        Caption: playerPhotoCaption,
        PhotoCardId: photoCardId,
      }
      try {
        await api.createDocument(
          Server.memeGamePhotoCaptionsCollectionId,
          data
        )
        .then(document => {
          setStale({ stale: true })
          return document
        }, error => console.log('create error', error));
      } catch (e) {
        console.log("Error adding game state", e);
      }
    }, [gameCode, participantName]
  );

  const handleJudgeCreated = useCallback(
    async (gameCode, judgeId, judgeName, recurrence) => {
      let data = {
        GameCode: gameCode,
        ParticipantID: judgeId,
        Recurrence: recurrence,
        Name: judgeName,
      }
      try {
        await api.createDocument(
          Server.memeJudgesCollectionId,
          data
        )
        .then(document => document, error => console.log('create error', error));
      } catch (e) {
        console.log("Error creating judge", e);
      }
    }
  );

  const handleGameStateCreate = useCallback(
    async () => {
      if (gameCode === '') {
        return;
      }

      try {
        const stateData = await api.listDocuments(
          Server.memeGameStateCollectionId,
        [
            Query.equal("GameCode", [gameCode]),
            Query.notEqual("Deleted", [true])
          ]
          )
          .then((res) => {
            if (res.documents.length > 0) {
              setMemeGameStateDocumentId(res.documents[0].$id);
            }

            return res.documents[0];
        }, error => {
          console.log(error)
          return error;
        });
      }
      catch (e) {
        console.log("Error getting game state", e);
        return;
      }

      let data = {
        GameCode: gameCode,
        Phase: phase,
        JudgeID: judgeId,
        PhotoCardID: photoCardId,
        Captions: captions,
      }
      try {
        await api.createDocument(
          Server.memeGameStateCollectionId,
          data
        )
        .then(document => document, error => console.log('create error', error));
      } catch (e) {
        console.log("Error adding game state", e);
      }
    }, [gameCode]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (gameCode === '') {
        return;
      }
      handleGameStateCreate().then(r => {
        console.log(r);
        clearTimeout(timer);
      });
    }, 1000);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (gameState?.Phase) {
        setPhase(gameState.Phase);
      }
      if (gameState?.JudgeID) {
        setJudgeId(gameState.JudgeID);
      }
      if (gameState?.PhotoCardID) {
        setPhotoCardId(gameState.PhotoCardID);
      }
    }, 2000);
  }, [gameState]);

  return (
     <section className="container h-screen mx-auto">
       <div className="p-6 mt-[10%] mx-[5%] border border-1 border-blue-800 border-solid rounded-xl transform -rotate-6">
         <div className="text-8xl font-bold text-blue-800 text-center">What do you meme?</div>
       </div>
       {/*{isLoadingGames || isLoadingGameCodes || isLoadingGameState ? (*/}
       {/*  <div className="w-screen h-screen fixed top-0 left-0 bg-gray-900 opacity-75 bg-[url('https://media.giphy.com/media/8agqybiK5LW8qrG3vJ/giphy.gif')] bg-center bg-no-repeat z-10">*/}
       {/*    <div className="w-[64px] h-[64px] border border-2 border-blue-400 rounded-lg animated animate-spin ease-linear" />*/}
       {/*  </div>*/}
       {/*) : (*/}
        <div className="grid grid-flex-row auto-rows-max">
          {renderPhase(
            handleGameStateChanged,
            handleSetStale,
            handleSetRecurrence,
            handleSetParticipantGameCode,
            handleSetParticipantName,
            handleSetPhase,
            handleSetJudge,
            handleSetJudgeId,
            handleUpdatePhotoCard,
            handleCaptionCreate,
            handleCaptionSelected,
            handleJudgeCreated,
            handleSetCaptionInput,
            handleGameCodeChanged,
            handleStartNewGame,
          )}
        </div>
      {/*)}*/}
    </section>
  );
};

export default MemeGame;
